import { apiCall } from './apiClient';

export type ManualEditOperation = 'replace' | 'insert' | 'remove';

export interface ManualEditChangeSummary {
  operation: ManualEditOperation;
  paragraph_index?: number;
  paragraph_reference?: string;
  explanation: string;
}

export interface ManualEditAiResult {
  status: 'applied' | 'no_change';
  summary: string;
  changes: ManualEditChangeSummary[];
  /** Canonical resulting DOCX as base64 when the server applied an edit. */
  docx_bytes?: string;
  document_fingerprint?: string;
  checkpoint_id?: string;
  /** Server-canonical, display-safe record persisted with a completed batch. */
  history_turn?: ManualEditAiHistoryTurn;
}

export interface ManualEditAiHistoryTurn {
  id: string;
  request: string;
  status: 'applied' | 'no_change';
  summary: string;
  changes: ManualEditChangeSummary[];
  submitted_by: string;
  completed_at: string;
  document_fingerprint_before: string;
  document_fingerprint_after?: string;
  checkpoint_id?: string;
}

export interface ManualEditAiHistory {
  turns: ManualEditAiHistoryTurn[];
  truncated: boolean;
}

export interface ManualEditAiRequest {
  request: string;
  document_fingerprint?: string;
}

export async function submitManualEditAiRequest(
  endpoint: string,
  request: ManualEditAiRequest,
): Promise<ManualEditAiResult> {
  // A disconnected response does not cancel server-side document persistence.
  // Repeating this POST could apply the same paragraph operation twice.
  const response = await apiCall<ManualEditAiResult>(endpoint, 'POST', request, { retries: 0, mutation: true });
  if (!response.success || !response.data) {
    const error = new Error(response.error || 'Unable to apply the requested edit.');
    (error as Error & { error_code?: string; technical_detail?: string }).error_code = response.error_code;
    (error as Error & { error_code?: string; technical_detail?: string }).technical_detail = response.technical_detail;
    if (response.request_outcome_unknown) {
      (error as Error & { request_outcome_unknown?: boolean }).request_outcome_unknown = true;
    }
    throw error;
  }
  return response.data;
}

/** Load the display-only request/result transcript for one Manual Edit stage. */
export async function getManualEditAiHistory(endpoint: string): Promise<ManualEditAiHistory> {
  const response = await apiCall<ManualEditAiHistory>(endpoint, 'GET');
  if (!response.success || !response.data) {
    throw new Error(response.error || 'Unable to load AI edit history.');
  }
  return response.data;
}

/** Decode the server's base64 DOCX response without exposing byte handling to UI components. */
export function decodeManualEditDocxBytes(base64: string): ArrayBuffer {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes.buffer;
}
